import {
    Prop,
    h,
    Component,
    State,
    Host,
    Watch,
    Element,
    forceUpdate,
} from "@stencil/core";
import api from "../../utils/api";
import { ServiceReportsResponse } from "./service-reports-response";
import { i18nProvider } from "@trumpf/ux.i18n";

@Component({
    tag: "ux-iws-service-reports",
    styleUrl: "service-reports.scss",
    shadow: false,
})
export class ServiceReports {
    @Element() domNode: HTMLUxIwsServiceReportsElement;

    /** the component loads data only when this attribute is not present */
    @Prop() lazyLoad: boolean = false;
    /** the url from which the content of the tab needs to be fetched */
    @Prop() src: string;
    /** optional language for translations (mainly used for storybook) - otherwise language is automatically detected by ux.i18n */
    @Prop() language?: string = "";

    @State() data: ServiceReportsResponse;

    private i18nSubscriberId: string;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    componentWillLoad() {
        if (this.language !== "") {
            i18nProvider.changeLanguage(this.language);
        }
    }

    async componentDidLoad() {
        this.loadData();
    }

    @Watch("lazyLoad")
    @Watch("src")
    loadData() {
        if (this.lazyLoad) return;
        api<ServiceReportsResponse>(this.src).then((data) => {
            this.data = data;
        });
    }

    render() {
        const { t, language } = i18nProvider;
        return (
            <Host class="ux-iws-service-reports" lang={language}>
                {this.data ? (
                    <div>
                        <div class="ux-tab__intro">
                            <div class="ux-text-headline ux-text-headline--big ux-u-text-align--center">
                                {t("serviceReports:title")}
                            </div>
                        </div>
                        <ul class="ux-iws-service-reports__list">
                            {this.data.serviceReports?.map((report) => {
                                const alias = !!report.equipmentAlias ? (
                                    <strong>{report.equipmentAlias}</strong>
                                ) : null;
                                return (
                                    <li>
                                        <div class="ux-tab__detail-wrapper">
                                            <div class="ux-tab__detail labeled-text">
                                                <div class="labeled-text__text-block">
                                                    <div class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">
                                                        {t(
                                                            "serviceReports:item.equipment"
                                                        )}
                                                    </div>
                                                    <div class="ux-text-normal">
                                                        {alias}{" "}
                                                        {report.equipmentName} –{" "}
                                                        {report.equipmentNumber}
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="ux-tab__detail labeled-text">
                                                <div class="labeled-text__text-block">
                                                    <div class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">
                                                        {t(
                                                            "serviceReports:item.technician"
                                                        )}
                                                    </div>
                                                    <div class="ux-text-normal">
                                                        {report.technicianName}
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="ux-tab__detail labeled-text">
                                                <div class="labeled-text__text-block">
                                                    <div class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">
                                                        {t(
                                                            "serviceReports:item.date"
                                                        )}
                                                    </div>
                                                    <div class="ux-text-normal">
                                                        <ux-date-time
                                                            utc={report.date}
                                                            format={t(
                                                                "serviceReports:item.date-format"
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {!!report.reportDownload ? (
                                                <div class="ux-tab__detail">
                                                    <div class="labeled-text__text-block">
                                                        <a
                                                            class="ux-button"
                                                            download=""
                                                            href={
                                                                report.reportDownload
                                                            }
                                                            data-testid="download"
                                                        >
                                                            {t(
                                                                "serviceReports:item.download"
                                                            )}{" "}
                                                            <ux-icon name="chevron-download"></ux-icon>
                                                        </a>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    </li>
                                );
                            })}
                            {this.data.serviceReports?.length === 0 ? (
                                <div
                                    class="ux-u-text-align--center"
                                    data-testid="no-reports"
                                >
                                    {t("serviceReports:noReports")}
                                </div>
                            ) : null}
                        </ul>
                    </div>
                ) : (
                    <ux-loader variant="spinner"></ux-loader>
                )}
            </Host>
        );
    }
}
